/**
 * Standalone keyboard shortcut handler functions for the fabric.js canvas editor.
 * Each function is importable and callable from EditorCanvas or any keyboard handler.
 */

import { getCanvasInstance } from './canvasInstance'
import { getClipboard, setClipboard } from './canvasClipboard'
import { useEditorStore } from '@/stores/useEditorStore'

const PASTE_OFFSET = 20

/**
 * Serializes the active object(s) to the module-level clipboard.
 */
export async function copySelected(): Promise<void> {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const activeObject = canvas.getActiveObject()
    if (!activeObject) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cloned = await activeObject.clone()
    setClipboard(JSON.stringify(cloned.toObject()))
}

/**
 * Deserializes from clipboard, offsets by (20,20), adds to canvas, and selects the new object.
 */
export async function pasteFromClipboard(): Promise<void> {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const clipboardJson = getClipboard()
    if (!clipboardJson) return

    const fabricModule = await import('fabric')
    const enlivenObjects = fabricModule.util.enlivenObjects

    let parsed: Record<string, unknown>
    try {
        parsed = JSON.parse(clipboardJson) as Record<string, unknown>
    } catch {
        return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const enlivened = await enlivenObjects([parsed]) as any[]
    if (!enlivened.length) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = enlivened[0] as any
    obj.set({
        left: (typeof obj.left === 'number' ? obj.left : 0) + PASTE_OFFSET,
        top: (typeof obj.top === 'number' ? obj.top : 0) + PASTE_OFFSET,
    })
    obj.setCoords()

    canvas.add(obj)
    canvas.setActiveObject(obj)
    canvas.requestRenderAll()

    useEditorStore.getState().pushUndo(JSON.stringify(canvas.toJSON()))
}

/**
 * Copies the active selection then removes it from the canvas.
 */
export async function cutSelected(): Promise<void> {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const activeObject = canvas.getActiveObject()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!activeObject || (activeObject as any)._isPageRect) return

    await copySelected()
    deleteSelected()
}

/**
 * Clones the active object, offsets it by (20,20), and adds it to the canvas.
 */
export async function duplicateSelected(): Promise<void> {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const activeObject = canvas.getActiveObject()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!activeObject || (activeObject as any)._isPageRect) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cloned = await activeObject.clone() as any
    cloned.set({
        left: (typeof cloned.left === 'number' ? cloned.left : 0) + PASTE_OFFSET,
        top: (typeof cloned.top === 'number' ? cloned.top : 0) + PASTE_OFFSET,
    })
    cloned.setCoords()

    canvas.add(cloned)
    canvas.setActiveObject(cloned)
    canvas.requestRenderAll()

    useEditorStore.getState().pushUndo(JSON.stringify(canvas.toJSON()))
}

/**
 * Selects all objects on the canvas except the page rect.
 * Returns a promise because creating an ActiveSelection requires fabric to be imported.
 */
export async function selectAll(): Promise<void> {
    const canvas = getCanvasInstance()
    if (!canvas) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectable = canvas.getObjects().filter((obj: any) => !obj._isPageRect)
    if (!selectable.length) return

    if (selectable.length === 1) {
        canvas.setActiveObject(selectable[0])
    } else {
        const { ActiveSelection } = await import('fabric')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selection = new ActiveSelection(selectable as any[], { canvas })
        canvas.setActiveObject(selection)
    }

    canvas.requestRenderAll()
}

/**
 * Moves all active objects by dx pixels horizontally and dy pixels vertically.
 */
export function nudgeSelected(dx: number, dy: number): void {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const activeObject = canvas.getActiveObject()
    if (!activeObject) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = activeObject as any

    if (obj.type === 'activeselection') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        obj.getObjects().forEach((child: any) => {
            if (!child._isPageRect) {
                child.set({
                    left: (child.left ?? 0) + dx,
                    top: (child.top ?? 0) + dy,
                })
                child.setCoords()
            }
        })
        obj.setCoords()
    } else if (!obj._isPageRect) {
        obj.set({
            left: (obj.left ?? 0) + dx,
            top: (obj.top ?? 0) + dy,
        })
        obj.setCoords()
    }

    canvas.requestRenderAll()
    useEditorStore.getState().pushUndo(JSON.stringify(canvas.toJSON()))
}

/**
 * Removes all active objects from the canvas, skipping the page rect.
 */
export function deleteSelected(): void {
    const canvas = getCanvasInstance()
    if (!canvas) return

    const activeObject = canvas.getActiveObject()
    if (!activeObject) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj = activeObject as any

    if (obj.type === 'activeselection') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const toRemove = obj.getObjects().filter((child: any) => !child._isPageRect)
        canvas.discardActiveObject()
        toRemove.forEach((child: unknown) => canvas.remove(child))
    } else if (!obj._isPageRect) {
        canvas.remove(activeObject)
    }

    canvas.requestRenderAll()
    useEditorStore.getState().pushUndo(JSON.stringify(canvas.toJSON()))
}

/**
 * Deselects all objects on the canvas.
 */
export function deselectAll(): void {
    const canvas = getCanvasInstance()
    if (!canvas) return

    canvas.discardActiveObject()
    canvas.requestRenderAll()
}
